import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { KeyRound, LogOut, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/context/ToastContext';
import ConfirmDialog from '@/components/ConfirmDialog';
import MfaSetup from '@/components/mfa/MfaSetup';
import BackupCodes from '@/components/mfa/BackupCodes';
import { BACKEND_URL } from '@/config';

const API = BACKEND_URL + '/api';
const errorText = (e, fallback) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : fallback);
const row = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' };

/** Saisie d'un code (application, ou code de secours si `backupAllowed`) avant une action sur la double authentification. */
function CodePrompt({ label, action, onCancel, backupAllowed = true, testid }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await action(code.trim()); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 12 }} data-testid={testid}>
      <input className="field-input" autoComplete="one-time-code" maxLength={backupAllowed ? 9 : 6} value={code}
        aria-label={backupAllowed ? 'Code de l’application ou code de secours' : 'Code de l’application'}
        placeholder={backupAllowed ? '123456 ou ABCD-EFGH' : '123456'}
        onChange={(e) => setCode(backupAllowed ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, ''))}
        style={{ maxWidth: 200, letterSpacing: 2 }} data-testid="mfa-action-code" />
      <button type="submit" className="btn btn-primary btn-sm" disabled={busy || code.length < 6} data-testid="mfa-action-submit">{label}</button>
      <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>Annuler</button>
    </form>
  );
}

/**
 * Sécurité du compte (/app/securite, /admin/securite) — double authentification (T15) et sessions (T16).
 * Obligatoire pour l'admin (elle ne se retire pas), facultative pour les membres. L'état vient du serveur
 * (`user.mfa` : enabled, required, backup_codes_left).
 */
export default function SecurityPage() {
  const { user, checkAuth } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const mfa = user?.mfa || { enabled: !!user?.mfa_enabled, required: false, backup_codes_left: null };
  const [mode, setMode] = useState('');        // '' | 'setup' | 'disable' | 'renew'
  const [newCodes, setNewCodes] = useState(null);
  const [confirmLogoutAll, setConfirmLogoutAll] = useState(false);

  const start = useCallback(() => axios.post(`${API}/auth/mfa/setup/start`, {}).then((r) => r.data), []);
  const confirm = useCallback((code) => axios.post(`${API}/auth/mfa/setup/confirm`, { code }).then((r) => r.data), []);

  const done = async (message) => {
    setMode('');
    toast.success(message);
    await checkAuth();
  };

  const disable = async (code) => {
    try {
      await axios.post(`${API}/auth/mfa/disable`, { code });
      await done('Double authentification retirée.');
    } catch (e) { toast.error(errorText(e, 'Code incorrect.')); }
  };

  const renew = async (code) => {
    try {
      const { data } = await axios.post(`${API}/auth/mfa/backup-codes`, { code });
      setMode('');
      setNewCodes(data.backup_codes);
    } catch (e) { toast.error(errorText(e, 'Code incorrect.')); }
  };

  const logoutAll = async () => {
    setConfirmLogoutAll(false);
    try {
      await axios.post(`${API}/auth/logout-all`);
    } catch (e) {
      toast.error(errorText(e, 'Les sessions n’ont pas pu être fermées. Réessayez.'));
      return;
    }
    await checkAuth();
    navigate('/login');
  };

  return (
    <div style={{ maxWidth: 720 }}>
      <div className="c2s-topbar">
        <div className="c2s-topbar-title">Sécurité du compte</div>
        <div className="c2s-topbar-sub">Double authentification et sessions ouvertes.</div>
      </div>

      <div className="card" style={{ marginBottom: 16 }} data-testid="security-mfa">
        <div style={row}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <ShieldCheck size={20} aria-hidden="true" />
            <div>
              <div className="section-title" style={{ margin: 0 }}>Double authentification</div>
              <div style={{ fontSize: 13, color: 'var(--ink2)' }}>
                {mfa.enabled
                  ? `Un code de votre application est demandé à chaque connexion. Codes de secours restants : ${mfa.backup_codes_left ?? '—'}.`
                  : 'Un code de votre application d’authentification, en plus du mot de passe, à chaque connexion.'}
                {mfa.required && ' Obligatoire pour ce compte.'}
              </div>
            </div>
          </div>
          <span className={`status status-${mfa.enabled ? 'completed' : 'draft'}`} data-testid="security-mfa-status">
            {mfa.enabled ? 'Activée' : 'Désactivée'}
          </span>
        </div>

        {newCodes && (
          <div style={{ marginTop: 14 }}>
            <BackupCodes codes={newCodes} onContinue={() => { setNewCodes(null); checkAuth(); }} continueLabel="Terminé" />
          </div>
        )}

        {!newCodes && !mfa.enabled && (mode === 'setup'
          ? <div style={{ marginTop: 14 }}><MfaSetup start={start} confirm={confirm} onDone={() => done('Double authentification activée.')} /></div>
          : <button type="button" className="btn btn-primary btn-sm" style={{ marginTop: 12 }} onClick={() => setMode('setup')} data-testid="security-mfa-enable">
              <KeyRound size={13} aria-hidden="true" /> Activer
            </button>)}

        {!newCodes && mfa.enabled && !mode && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setMode('renew')} data-testid="security-mfa-renew">
              Nouveaux codes de secours
            </button>
            {!mfa.required && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setMode('disable')} data-testid="security-mfa-disable">
                Retirer
              </button>
            )}
          </div>
        )}
        {mode === 'renew' && <CodePrompt label="Remplacer mes codes" action={renew} onCancel={() => setMode('')} backupAllowed={false} testid="security-mfa-renew-form" />}
        {mode === 'disable' && <CodePrompt label="Retirer" action={disable} onCancel={() => setMode('')} testid="security-mfa-disable-form" />}
      </div>

      <div className="card" data-testid="security-sessions">
        <div style={row}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <LogOut size={20} aria-hidden="true" />
            <div>
              <div className="section-title" style={{ margin: 0 }}>Sessions ouvertes</div>
              <div style={{ fontSize: 13, color: 'var(--ink2)' }}>
                Téléphone perdu, ordinateur partagé : fermez toutes les sessions, celle-ci comprise.
              </div>
            </div>
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setConfirmLogoutAll(true)} data-testid="security-logout-all">
            Me déconnecter de tous les appareils
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmLogoutAll}
        variant="destructive"
        icon="ban"
        title="Fermer toutes les sessions ?"
        subtitle="Vous serez déconnecté partout, y compris ici. Il faudra vous reconnecter."
        confirmLabel="Tout fermer"
        onConfirm={logoutAll}
        onClose={() => setConfirmLogoutAll(false)}
      />
    </div>
  );
}
